
import React from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Edit, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { type RetentionPolicy } from '@/components/admin/certificates/types';

interface PoliciesTableProps {
  policies: RetentionPolicy[];
  onEditPolicy: (policy: RetentionPolicy) => void;
  onDeletePolicy?: (policyId: string) => void;
}

export function PoliciesTable({ policies, onEditPolicy, onDeletePolicy }: PoliciesTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Table Name</TableHead>
          <TableHead>State</TableHead>
          <TableHead>Retention Years</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Description</TableHead>
          <TableHead>Created</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {policies.length === 0 ? (
          <TableRow>
            <TableCell colSpan={7} className="text-center py-6 text-muted-foreground">
              No retention policies found. Create a new policy to get started.
            </TableCell>
          </TableRow>
        ) : (
          policies.map((policy) => (
            <TableRow key={policy.id}>
              <TableCell className="font-medium">
                {policy.table_name}
              </TableCell>
              <TableCell>
                {policy.state || 'All States'}
              </TableCell>
              <TableCell>
                {policy.retention_years} years
              </TableCell>
              <TableCell>
                {policy.is_active ? (
                  <Badge variant="outline" className="bg-green-50 text-green-700 hover:bg-green-50 border-green-200">
                    Active
                  </Badge>
                ) : (
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 hover:bg-amber-50 border-amber-200">
                    Inactive
                  </Badge>
                )}
              </TableCell>
              <TableCell>
                {policy.description || 'No description'}
              </TableCell>
              <TableCell>
                {new Date(policy.created_at).toLocaleDateString()}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => onEditPolicy(policy)}
                  >
                    <Edit className="h-4 w-4" />
                    <span className="sr-only">Edit policy</span>
                  </Button>
                  
                  {onDeletePolicy && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-destructive">
                          <Trash2 className="h-4 w-4" />
                          <span className="sr-only">Delete policy</span>
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Retention Policy</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete this retention policy for {policy.table_name}
                            {policy.state ? ` (${policy.state})` : ''}? 
                            This action cannot be undone and will remove the policy from the system.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => onDeletePolicy(policy.id)}
                            className="bg-destructive hover:bg-destructive/90"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
